import { Component, OnInit, Injectable } from '@angular/core'
import {FormGroup, FormBuilder} from '@angular/forms'
import {Router, ActivatedRoute, CanDeactivate, ActivatedRouteSnapshot, RouterStateSnapshot} from '@angular/router'
import { BaseComponent } from '../../../components/base.component'
import { ValidationService } from '../../../services/validation-service'
import {Customer, Account} from '../../../types'
import { CustomerActions } from '../../../actions/customer-actions'
import { infoToast } from '../../../toaster'
import {possessive, isAgencySignup} from '../../../utils'
import sweetAlert from '../../../bmAlert'
import { Title } from '@angular/platform-browser'

@Component({
  selector: `online-presence-page-v1`,
  template: `
    <split-view>
      <div left>
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-12 mt-3">
              <div class="nc-section-icon fas fa-comment"></div>
              <h1 class="nc-section-header">
                About {{ customer?.businessName }}
              </h1>

              <p *ngIf="!isAgencySignup()" class="top-buffer">Tell us about your business.</p>
              <p *ngIf="!isAgencySignup()" class="top-buffer">Your business and brand are unique. Tell our writers more so they can create content that fits your voice.</p>
              <p *ngIf="isAgencySignup()" class="top-buffer">Your client's business and brand are unique. Tell our writers more so they can create content that fits your client's voice.</p>
              <about-nav-v1 [customer]="customer"></about-nav-v1>
            </div>
          </div>
        </div>
      </div>
      <div right>
        <div class="container-fluid big-top-buffer">
          <div class="row">
            <div class="col-sm-12">
              <h2>{{account?.agency ? possessive(customer?.businessName) : 'Your'}} Online Presence</h2>
              <p class="top-buffer bottom-buffer">Sharing this info will allow our writers to better understand {{account?.agency ? "your client's" : "your business'"}} tone, find ideas and avoid topics that have already been done.</p>
            </div>
          </div>
          <form [formGroup]="writerResearchForm">
            <div class="row">
              <div class="col-sm-12">
                <fieldset class="form-group">
                  <label for="website">Website URL</label>
                  <input id="website" class="form-control" formControlName="website" placeholder="Ex: http://boulderflowers.com">
                  <div [hidden]="writerResearchForm.controls.website.valid || writerResearchForm.controls.website.untouched" class="tiny-top-buffer text-danger">
                    Not a valid url
                  </div>
                </fieldset>
              </div>
            </div>
            <div class="row">
              <div class="col-sm-12">
                <fieldset class="form-group">
                  <label for="blog">Blog URL</label>
                  <input id="blog" class="form-control" formControlName="blog" placeholder="Ex: http://boulderflowers.com/blog">
                  <div [hidden]="writerResearchForm.controls.blog.valid || writerResearchForm.controls.blog.untouched" class="tiny-top-buffer text-danger">
                    Not a valid url
                  </div>
                </fieldset>
              </div>
            </div>

            <div class="row">
              <div class="col-sm-12">
                <h4>Social Media</h4>
              </div>
            </div>

            <div class="row">
              <div class="col-sm-12">
                <fieldset class="form-group">
                  <label for="twitter">Twitter Handle</label>
                  <div class="input-group">
                    <div class="input-group-prepend">
                      <span class="input-group-text">
                        <i class="fas fa-at"></i>
                      </span>
                    </div>
                    <input id="twitter" class="form-control" formControlName="twitter" placeholder="Ex: BoulderFlowerCo">
                  </div>
                  <div [hidden]="writerResearchForm.controls.twitter.valid || writerResearchForm.controls.twitter.untouched" class="tiny-top-buffer text-danger">
                    Not a valid twitter handle
                  </div>
                </fieldset>
              </div>
            </div>

            <div class="row">
              <div class="col-sm-12">
                <fieldset class="form-group">
                  <label for="facebook" class="tiny-top-buffer">Facebook Username</label>
                  <div class="input-group">
                    <div class="input-group-prepend">
                      <span class="input-group-text">
                        <i class="fas fa-at"></i>
                      </span>
                    </div>
                    <input id="facebook" class="form-control" formControlName="facebook" placeholder="Ex: BoulderFlowerCo">
                  </div>
                  <div [hidden]="writerResearchForm.controls.facebook.valid || writerResearchForm.controls.facebook.untouched" class="tiny-top-buffer text-danger">
                    Not a valid facebook username
                  </div>
                </fieldset>
              </div>
            </div>
          </form>

          <div class="row">
            <div class="col-sm-12">
              <div class="top-buffer bottom-buffer">
                <button (click)="updateCustomer()" class="block btn btn-primary mr-2">Save & continue</button>
                <button (click)="next()" class="block btn btn-default">Skip for now</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </split-view>
  `
})
export class OnlinePresencePageV1 extends BaseComponent implements OnInit {
  writerResearchForm: FormGroup;
  customerId: number;
  customer: Customer;
  submitted: boolean = false;
  account: Account;

  constructor(private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private customerActions: CustomerActions,
    private titleService: Title) {
    super()
  }

  ngOnInit(): void {
    this.titleService.setTitle('Sign Up - Online Presence')
    const routeObs = this.route.params.subscribe(params => {
      this.clearRouteObservers()
      this.customerId = +params['customerId']
      this.customerActions.loadCustomer(this.customerId)

      this.observeCustomer(this.customerId, c => {
        this.customer = c
        this.resetForm()
      })
    })
    this.observeAccount(a => this.account = a)
    this.observers.push(routeObs)
  }

  resetForm(): void {
    this.writerResearchForm = this.formBuilder.group({
      website: [this.customer ? this.customer.website : "", ValidationService.urlValidator],
      blog: [this.customer ? this.customer.blog : "", ValidationService.urlValidator],
      twitter: [this.customer ? this.customer.twitter : "", ValidationService.twitterHandleValidator],
      facebook: [this.customer ? this.customer.facebook : "", ValidationService.facebookUsernameValidator]
    })
  }

  updateCustomer(): void {
    if (this.writerResearchForm.valid) {
      this.submitted = true
      this.customerActions.updateCustomer(this.customerId, this.writerResearchForm.value).then(() => {
        this.next()
      })
    } else {
      infoToast('Please correct errors above to save')
    }
  }

  next(): void {
    this.router.navigate(['../', 'audience'], { relativeTo: this.route })
  }

  possessive(str: string): string {
    return possessive(str)
  }

  isAgencySignup(): boolean {
    return isAgencySignup(this.account)
  }
}

@Injectable()
export class CanDeactivateOnlinePresencePageV1 implements CanDeactivate<OnlinePresencePageV1> {
  canDeactivate(component: OnlinePresencePageV1, route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean>|boolean {
    if (component.writerResearchForm.dirty && !component.submitted) {
      return sweetAlert.confirm({text: "You have unsaved changes, are you sure you want to continue?"})
    } else {
      return true
    }
  }
}
